"use client";

import { useEffect, useRef, useState } from "react";

export type AnimatedCounterProps = {
  value: string;
  className?: string;
  durationMs?: number;
};

function parseStatValue(value: string): {
  prefix: string;
  target: number;
  suffix: string;
} {
  const match = value.trim().match(/^([^0-9]*)([\d,]+)(.*)$/);
  if (!match) {
    return { prefix: "", target: 0, suffix: value };
  }

  return {
    prefix: match[1],
    target: Number(match[2].replace(/,/g, "")),
    suffix: match[3],
  };
}

export function AnimatedCounter({
  value,
  className,
  durationMs = 2000,
}: AnimatedCounterProps) {
  const { prefix, target, suffix } = parseStatValue(value);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;

        startedRef.current = true;
        observer.disconnect();

        let startTime: number | null = null;

        const animate = (timestamp: number) => {
          if (startTime === null) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / durationMs, 1);
          setDisplay(Math.floor(progress * target));

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setDisplay(target);
          }
        };

        requestAnimationFrame(animate);
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [durationMs, target]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toLocaleString()}
      {suffix ? <b>{suffix}</b> : null}
    </span>
  );
}
